import { autocompletion, CompletionContext } from '@codemirror/autocomplete'
import { esLint, javascript } from '@codemirror/lang-javascript'
import { linter, lintGutter } from '@codemirror/lint'
import js from '@eslint/js'
import { InfoRounded } from '@mui/icons-material'
import { Box, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import CodeMirror from '@uiw/react-codemirror'
import * as eslint from 'eslint-linter-browserify'
import { ReactNode } from 'react'
import algo from '../../../core/Algo'
import { IFlow, INode, NodeType } from '../../../definitions/Nodes'
import { IAcceptedData } from '../../../definitions/NodesDescription'
import NodeManager from '../../../managers/NodeManager'
import { useAppSelector } from '../../../store/Hooks'
import JSONEditor from '../../editor/JSONEditor'

interface P {
    acceptedData: IAcceptedData<NodeType>
    selectedNode: INode<NodeType>
    setSelected: (node: INode) => void
    disable: boolean
    flow: IFlow
}

const getListAutocomplete = (flow: IFlow, selectedNode: INode<NodeType>) => {
    const results = []

    if (Object.keys(flow.globals).length === 0) return results
    const globalKeys = Object.keys(flow.globals)

    for (const globalKey of globalKeys) {
        results.push({
            label: 'globals' + '.' + globalKey,
            type: 'globals',
            value: 'globals' + '.' + globalKey
        })
    }

    if (selectedNode.inputs.length === 0) return results

    for (let index = 0; index < selectedNode.inputs.length; index++) {
        const input = selectedNode.inputs[index]
        const inputNode = flow.nodes.find(k => k.uuid === input.uuid)
        const schemaKeys = (
            inputNode?.jsonSchema?.items?.properties
                ? Object.keys(inputNode?.jsonSchema?.items?.properties ?? {})
                : Object.keys(inputNode?.jsonSchema?.properties ?? {})
        ) ?? null
        if (!schemaKeys) continue
        results.push({
            label: `${inputNode.name}.${input.argumentKey}`,
            type: inputNode.type,
            apply: `inputValues[${index}].${input.argumentKey}`
        })
    }

    return results
}

function NodeJSEditor({ acceptedData, selectedNode, setSelected, disable, flow }: P) {
    const theme = useTheme()
    const value: string = selectedNode.data[acceptedData.parameter]
    const errorNoInput = acceptedData.isRequired && (!algo.hasVal(value) || value.length === 0)
    const mode = useAppSelector(state => state.app.mode)

    const listAutocompletion = getListAutocomplete(flow, selectedNode)
    const shapes = NodeManager.getInputShapes(selectedNode, flow.nodes)

    const renderTooltip = (title: ReactNode, disable: boolean) => (
        <Tooltip arrow title={title} disableHoverListener={disable} slotProps={{ tooltip: { sx: { maxWidth: 500, maxHeight: 500, padding: 0, margin: 0, backgroundColor: theme.palette.background.paper } } }}>
            <InfoRounded sx={{ cursor: disable ? undefined : 'pointer', color: disable ? 'var(--icon-disabled)' : 'var(--icon-color)' }} />
        </Tooltip>
    )

    const myCompletions = (context: CompletionContext) => {
        const word = context.matchBefore(/\w*/)
        if (word.from === word.to && !context.explicit) return null
        return {
            from: word.from,
            options: [...listAutocompletion]
        }
    }

    const config = [
        js.configs.recommended,
        {
            rules: {
                'no-undef': 'error',
                'no-unused-vars': 'off',
                'no-console': 'off'
            },
            languageOptions: {
                ecmaVersion: 'latest',
                globals: {
                    VARIABLES: 'readonly'
                },
                parserOptions: {
                    ecmaVersion: 2022,
                    sourceType: 'module',
                    ecmaFeatures: {
                        globalReturn: true
                    }
                }
            }
        }
    ]

    return (
        <Stack spacing={2}>
            <Stack flexDirection='row' gap={1}>
                {renderTooltip(
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, padding: 2 }}>
                        <Typography>Input Values</Typography>
                        <JSONEditor value={shapes?.[0]?.shape ?? {}} readOnly={false} />
                    </Box>,
                    disable)}
                <Typography variant='subtitle2'>{acceptedData?.header ?? null}</Typography>
            </Stack>
            <CodeMirror
                readOnly={disable}
                theme={mode === 'dark' ? 'dark' : 'light'}
                width='100%'
                minHeight='30vh'
                style={{ color: 'red', colorScheme: 'dark', border: errorNoInput ? '1px solid red' : undefined, flex: 1, height: '100%', display: 'flex', overflow: 'auto' }}
                value={value}
                extensions={[
                    javascript({ jsx: true, typescript: true }),
                    lintGutter(),
                    linter(esLint(new eslint.Linter(), config)),
                    autocompletion({ override: [myCompletions] })
                ]}
                onChange={(value: string) => {
                    setSelected({ ...selectedNode, data: { ...selectedNode.data, [acceptedData.parameter]: value } })
                }}
            />
        </Stack>
    )
}

export default NodeJSEditor
